/**
 * Widget title normalization, shared by every surface that displays a widget's
 * name — the rendered card heading and the Settings → Dashboard Management
 * lists.
 *
 * It lives in its own module rather than inside WidgetRenderer because the
 * settings page must not pull the chart bundle in just to label a row.
 */

/** Heading the "Resolved By" widget falls back to when no title is stored. */
export const RESOLUTION_SOURCE_TITLE = 'Resolved By';

/**
 * Retired catalog names for the "Resolved By" widget.
 *
 * A widget's title is COPIED from the catalog item and persisted at add-time
 * (`title: catalogItem.name`), and the persisted value wins over the widget's
 * own default. So renaming the catalog entry only reaches newly-added widgets —
 * every dashboard that already had this one would keep showing the old
 * "… (Automation vs Agent)" heading forever.
 *
 * Treating a stored title that still equals a retired name as "not customised"
 * lets those dashboards pick up the new heading with no migration, while a
 * title the admin actually typed is left alone.
 */
const RETIRED_RESOLUTION_SOURCE_TITLES = new Set([
  'Resolved By (Automation vs Agent)',
]);

/** Returns undefined for a retired name so the widget's own default applies. */
export function normalizeResolutionSourceTitle(title?: string): string | undefined {
  if (title && RETIRED_RESOLUTION_SOURCE_TITLES.has(title.trim())) return undefined;
  return title;
}

/**
 * Title to SHOW for a widget. Unlike the normalizer this never returns
 * undefined — callers that render a bare label (a list row, a tooltip) have no
 * component default to fall back to, so the retired name is resolved all the
 * way to the current heading here.
 */
export function widgetDisplayTitle(widgetType: string, title?: string): string {
  if (widgetType === 'chart_resolution_source') {
    return normalizeResolutionSourceTitle(title) ?? RESOLUTION_SOURCE_TITLE;
  }
  return title ?? '';
}
